import { Alert, Badge, Button, Card, Col, Form, Input, InputNumber, notification, Row, Spin, Tooltip } from 'antd';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Coatings, Finishes, Materials, Processes } from '../DropDowns';
import QuoteStore from '../../stores/QuoteStore';
import { CoatingsProps, FinishesProps, QuoteLineItemProps } from '@partsbadger/types';
import { CalculatingPriceState } from './CalculatingPriceState';
import { observer } from 'mobx-react';
import SuggestionField from '../SuggestionField';
import { CloseOutlined, LoadingOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { QuestionMark } from '../QuestionMark';

import {
    ErrorRender,
    FormErrorParser,
    FrequencySelector,
    ORDER_FREQUENCY_ONE_TIME,
    frequencyImpliesBatchShipment,
} from '@partsbadger/library';
import PlasticMaterialPreferencesModal from './PlasticMaterialPreferencesModal';
import LeadTimeOptions from './LeadTimeOptions';
import { MultipleQuantitiesPrices } from '../MultipleQuantitiesPrices';
import { PricingFeedback } from '../PricingFeedback';
import { areDrawingSuggestionsLoading } from '../../utils/drawingSuggestions';
import { getPricingState } from '../../utils/pricingState';
import QuantitySelector from './QuantitySelector';
import debounce from 'lodash/debounce';
import { isCustomCoatingSelected, isCustomFinishSelected, isCustomMaterialSelected } from '../../utils/util';
import { getLineItemErrorFields, withoutFilledFields } from '../../utils/partStatus';
import { valuesForScheduledSave } from '../../utils/scheduledSaveValues';
import { useDebouncedAction } from '../../utils/useDebouncedAction';
import { getMaterialPayload, getMaterialSelectionOutcome, materialStillToClear } from '../../utils/materialSelection';

interface Props {
    index: number;
    item: QuoteLineItemProps;
    /**
     * When this item is the base of a "multiple quantities" group, the sibling
     * variations (one per quantity) are passed here so the prices table can fan
     * them back into a single view instead of rendering separate cards.
     */
    multiQuantityItems?: Array<QuoteLineItemProps>;
}

const min = 1;

function initialOrderFrequencyFromLineItem(item: QuoteLineItemProps): string {
    if (item.quantity_frequency) {
        return item.quantity_frequency;
    }
    return item.batch_shipment ? 'monthly' : ORDER_FREQUENCY_ONE_TIME;
}

const ManualQuoteRequiredAlert = ({ quantity, lineItemId }: { quantity?: number; lineItemId?: number }) => (
    <div data-needs-review="true" data-quantity={quantity} data-testid="quantity-manual-review">
        <Alert
            className="manual-review-required-message manual-quote-required-alert"
            data-testid="manual-quote-alert"
            description="No instant price is available. Our team will provide pricing and lead time."
            message={
                <strong>
                    {quantity ? `${quantity} ${quantity === 1 ? 'unit' : 'units'} · ` : ''}Manual quote required
                    <PricingFeedback lineItemId={lineItemId} />
                </strong>
            }
            showIcon={false}
            style={{ width: '100%', borderRadius: 8 }}
            type="info"
        />
    </div>
);

const DrawingSuggestionsLoading = ({ suggestionType }: { suggestionType: string }) => (
    <div
        className="d-flex align-items-center mt-2 text-muted"
        data-suggestion-type={suggestionType}
        data-testid="drawing-suggestions-loading"
        aria-live="polite"
        style={{ color: '#8c8c8c', fontSize: 11, opacity: 0.75 }}
    >
        <Spin
            className="mr-2"
            indicator={<LoadingOutlined spin style={{ color: '#bfbfbf', fontSize: 12 }} />}
            size="small"
        />
        <span>Loading suggestions…</span>
    </div>
);

const QuoteLineItem: React.FC<Props> = observer(({ index, item, multiQuantityItems, ...props }) => {
    const [loading, setLoading] = useState(false);
    const [showMaterialColorModal, setShowMaterialColorModal] = useState<string | null>(null);
    /** Scope for “don’t clobber form from WebSocket while user is editing”. */
    const variantFormShellRef = useRef<HTMLDivElement>(null);

    const product = item?.product;

    const [form] = Form.useForm();
    const { getFieldValue, setFieldsValue } = form;

    const { needs_manual_quote } = item;
    const { analysis_status } = item?.product;

    // "priced" is also false while the analyses that decide FastLane and Standard eligibility run:
    // the prices of a part can still change, so they are shown as calculating until then.
    const isSafeAutoQuote = getPricingState(item) === 'priced';
    // Geometry ready but the async pricing analysis has not concluded yet: show
    // a transitional "Calculating prices…" state instead of the manual-quote alert.
    const isPricingInProgress = getPricingState(item) === 'calculating';

    const files_2d = product?.attachments?.files_2d;
    const suggestionsLoading = areDrawingSuggestionsLoading(product);

    // Parts sharing a material already loaded start with its coatings, without a re-render.
    const [coatings, setCoatings] = useState<CoatingsProps[]>(() => {
        const materialId = product?.material_related?.id;
        return (materialId && QuoteStore.getCachedCoatingsByMaterial(Number(materialId))) || [];
    });
    const selectedMaterial = Form.useWatch(`material__${item.id}`, form);
    const selectedFinish = Form.useWatch(`finishes__${item.id}`, form);
    const selectedCoating = Form.useWatch(`coatings__${item.id}`, form);
    const showCustomMaterialInput = isCustomMaterialSelected(item, QuoteStore.materials, selectedMaterial);
    const showCustomFinishInput = isCustomFinishSelected(item, QuoteStore.finishes, selectedFinish);
    const showCustomCoatingInput = isCustomCoatingSelected(item, coatings, selectedCoating);
    const customMaterialValue = Form.useWatch(`custom_material__${item.id}`, form);
    const customFinishValue = Form.useWatch(`custom_finish__${item.id}`, form);
    const customCoatingValue = Form.useWatch(`custom_coating__${item.id}`, form);

    // Required-spec errors of this part only (reading QuoteStore.errorFields would re-render
    // every part on each save). A field the user already filled is not flagged while its
    // value is being saved.
    const itemErrorFields = withoutFilledFields(
        getLineItemErrorFields(item, { materials: QuoteStore.materials, finishes: QuoteStore.finishes, coatings }),
        {
            [`material__${item.id}`]: selectedMaterial,
            [`finishes__${item.id}`]: selectedFinish,
            [`coatings__${item.id}`]: selectedCoating,
            [`custom_material__${item.id}`]: customMaterialValue,
            [`custom_finish__${item.id}`]: customFinishValue,
            [`custom_coating__${item.id}`]: customCoatingValue,
        }
    );
    const hasFieldError = (name: string) => itemErrorFields.some(error => error.fieldName === `${name}__${item.id}`);
    // Spinner for this part's prices only, not for every part while any part saves.
    const isSaving =
        loading ||
        QuoteStore.isLineItemSaving(item.id) ||
        Boolean(multiQuantityItems?.some(sibling => QuoteStore.isLineItemSaving(sibling.id)));

    /**
     * Toggle between "single quantity" and "multiple quantities" mode.
     *
     * The mode is persisted on the backend via the `needs_more_quantities` flag
     * (which is inert for pricing — only a non-empty `quantities` array trips the
     * safe-auto-quote guards). Initialising from `item.needs_more_quantities`
     * means the multi-quantity view survives a page refresh.
     */
    const [quoteMultipleMode, setQuoteMultipleMode] = useState<boolean>(!!item.needs_more_quantities);
    const [displayedQuantities, setDisplayedQuantities] = useState<number[]>([Math.max(1, Number(item.quantity) || 1)]);

    useEffect(() => {
        setQuoteMultipleMode(!!item.needs_more_quantities);
    }, [item.needs_more_quantities]);

    const fileType =
        item.product.has_file_3d && item.product.attachments.file3d.filename
            ? item.product.attachments.file3d.filename.split('.').pop()
            : null;

    useEffect(() => {
        const shell = variantFormShellRef.current;
        if (
            typeof document !== 'undefined' &&
            document.activeElement instanceof Node &&
            shell?.contains(document.activeElement)
        ) {
            // Line item refreshed (e.g. WebSocket `setLineItem`) while focus is in this variant’s form —
            // skip pushing store snapshot into the form or in-progress custom text / quantities reset.
            return;
        }

        setFieldsValue({
            [fieldName('manufacturing_type')]:
                fileType?.toLowerCase() === 'stl' ? '3D PRINTING' : item.manufacturing_type,
            [fieldName('material')]: {
                key: product.material_related?.id,
                value: product.material_related?.id,
                label: product.material_related?.name,
            },
            [fieldName('custom_material')]: product.custom_material,
            [fieldName('finishes')]: {
                key: product.finish_related?.id,
                value: product.finish_related?.id,
                label: product.finish_related?.name,
            },
            [fieldName('custom_finish')]: product?.custom_finish,
            [fieldName('coatings')]: {
                key: product?.coating_related?.id,
                value: product?.coating_related?.id,
                label: product?.coating_related?.name,
            },
            [fieldName('custom_coating')]: product?.custom_coating,
            [fieldName('quantity')]: item.quantity,
            // `item.quantities` is inert — the backend field is always sent
            // as `[]` (see handleSubmit below), so writing it here on every
            // `item` reference change (which happens on nearly every store
            // update, e.g. WebSocket pushes) would wipe out the multi-quantity
            // tags input right after it's populated by the sibling-quantities
            // effect below. The real source of truth for that field is the
            // set of sibling variations, handled separately.
        });
    }, [item]);

    useEffect(() => {
        // Ignore a response that arrives after the material changed again.
        let current = true;
        const loadCoatings = async () => {
            const materialId = product?.material_related?.id;
            if (materialId) {
                const cachedCoatings = QuoteStore.getCachedCoatingsByMaterial(Number(materialId));
                if (cachedCoatings) {
                    setCoatings(cachedCoatings);
                    return;
                }
                try {
                    const compatibleCoatings = await QuoteStore.getCoatingsByMaterial(Number(materialId));
                    if (current && compatibleCoatings) setCoatings(compatibleCoatings);
                } catch (error) {
                    console.error('Error loading coatings:', error);
                    if (current) setCoatings([]);
                }
            } else {
                setCoatings([]);
            }
        };
        loadCoatings();
        return () => {
            current = false;
        };
    }, [product?.material_related?.id]);

    // False from the moment the card unmounts (layout cleanups run before the debounced
    // save is flushed), so that save uses its snapshot instead of the unregistered fields.
    const mounted = useRef(true);
    // Set when a process change empties the material, so the saves clear it on the line too (until
    // a material is chosen: a failed save must not lose it).
    const materialClearedByProcess = useRef(false);
    useLayoutEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    const handleSubmit = async (snapshot?: Record<string, any>, quoteUid?: string) => {
        valuesForScheduledSave(mounted.current, snapshot, () => form.validateFields())
            .then(async values => {
                const payload = {
                    id: item.id,
                    product: {
                        customs: { ...item.product.customs },
                    },

                    // Multiple-quantities is simulated via item variations (fan-out), so the
                    // line item itself must never persist a `quantities` array or the
                    // safe-auto-quote guards would drop it to manual quote. Always send an
                    // empty array. `needs_more_quantities` carries the persisted UI mode
                    // (multi vs single) without affecting pricing.
                    needs_more_quantities: quoteMultipleMode,
                    quantities: [],
                    quantity: values[fieldName('quantity')],
                    delivery: values[fieldName('delivery')],
                    manufacturing_type: values[fieldName('manufacturing_type')],
                    manual_adjustment: values[fieldName('manual_adjustment')],
                    quantity_frequency: values[fieldName('quantity_frequency')] ?? ORDER_FREQUENCY_ONE_TIME,
                    batch_shipment: frequencyImpliesBatchShipment(
                        values[fieldName('quantity_frequency')] ?? ORDER_FREQUENCY_ONE_TIME
                    ),
                };

                if (values[fieldName('isTransparent')] == 'yes') {
                    const finish = QuoteStore.finishes?.find(f => f.name === 'Flame Polish');

                    payload['product']['finishes'] = [finish?.id];
                } else {
                    payload['product']['finishes'] = values[fieldName('finishes')]?.key
                        ? [values[fieldName('finishes')]?.key]
                        : [];
                }

                if (values[fieldName('preferredColor')]) {
                    const materialName = values[fieldName('material')]?.label;
                    const customMaterial = `${materialName} ${values[fieldName('preferredColor')]}`;

                    const material_values = QuoteStore.materials?.find(f => f.name === 'Custom');

                    payload['product']['material'] = material_values?.id;
                    payload['product']['custom_material'] = customMaterial;

                    form.resetFields([fieldName('preferredColor')]);
                } else {
                    const material = getMaterialPayload(
                        values[fieldName('material')]?.key,
                        materialClearedByProcess.current
                    );
                    if (material !== undefined) payload['product']['material'] = material;
                    materialClearedByProcess.current = materialStillToClear(
                        values[fieldName('material')]?.key,
                        materialClearedByProcess.current
                    );
                    if (values[fieldName('custom_material')] || values[fieldName('custom_material')] === '') {
                        payload['product']['custom_material'] = values[fieldName('custom_material')];
                    }
                }

                payload['product']['coatings'] = values[fieldName('coatings')]?.key
                    ? [values[fieldName('coatings')]?.key]
                    : [];

                if (values[fieldName('custom_finish')] !== null) {
                    payload['product']['custom_finish'] = values[fieldName('custom_finish')];
                }

                if (values[fieldName('custom_coating')] !== null) {
                    payload['product']['custom_coating'] = values[fieldName('custom_coating')];
                }

                if (values[fieldName('lead_time_options')]) {
                    // payload['lead_time'] = values[fieldName('lead_time_options')];

                    // const priceId = item?.pricing_options?.prices_by_quantity?.[item?.quantity]?.find(
                    //     option => option.lead_time === values[fieldName('lead_time_options')]
                    // )?.id;

                    payload['price_id'] = values[fieldName('lead_time_options')];
                }

                setLoading(true);
                setShowMaterialColorModal(null);
                form.resetFields([fieldName('isTransparent')]);

                // In multiple-quantities mode the variation attributes (material,
                // finish, coating, process...) must stay in sync across every
                // quantity of the group, so propagate the change to all siblings.
                // A save flushed after another quote was opened goes to this card's quote.
                const updatePromise = QuoteStore.saveLineItemToQuote(
                    quoteUid ?? QuoteStore.quote_uid,
                    payload,
                    multiQuantityItems ?? []
                );

                await updatePromise
                    .then(() => {
                        setLoading(false);
                    })
                    .catch(error => {
                        setLoading(false);

                        notification.error({
                            message: 'Error',
                            description: <ErrorRender error={error} />,
                        });
                    });
            })
            .catch(errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
                setLoading(false);
            });
    };

    const fieldName = (name: string) => {
        return name + '__' + item.id;
    };

    // In multiple-quantities mode the source of truth for "which quantities" is
    // the set of sibling variations (each a single-quantity line item), not the
    // inert `quantities` field. Keep the chip selector in sync with them so the
    // selection survives a page refresh / getQuote reload.
    const siblingQuantities = Array.from(
        new Set((multiQuantityItems ?? []).map(i => i.quantity).filter(q => Number.isFinite(q)))
    ).sort((a, b) => a - b);
    const siblingQuantitiesKey = siblingQuantities.join(',');

    useEffect(() => {
        const quantities = siblingQuantities.length ? siblingQuantities : [Math.max(1, Number(item.quantity) || 1)];
        // The hidden quantity field must reflect THIS card's own line item, not
        // the lowest sibling value — the base card (lowest id) isn't necessarily
        // the lowest-quantity member, and overwriting it here would silently
        // change this item's quantity on the next unrelated save.
        const currentQuantity = Math.max(1, Number(item.quantity) || quantities[0]);
        setDisplayedQuantities(quantities);
        setFieldsValue({
            [fieldName('quantity')]: currentQuantity,
            [fieldName('quantities')]: quantities,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.quantity, siblingQuantitiesKey]);

    const handleModalCancel = () => {
        setShowMaterialColorModal(null);
        form.resetFields([fieldName('material'), fieldName('isTransparent')]);
    };

    // A change made just before the card goes away (another part opened, quote left) is still
    // saved: the pending save runs on unmount with the form values and the quote taken when it
    // was scheduled. The form stores a value before calling these handlers, so the snapshot is
    // current.
    const { schedule: scheduleSubmit } = useDebouncedAction(handleSubmit, { flushOnUnmount: true });

    // Text inputs and quick successive edits are grouped into one save.
    const handleChange = () => scheduleSubmit(400, form.getFieldsValue(true), QuoteStore.quote_uid);

    // A dropdown choice is final: save right away instead of waiting for the debounce.
    // The timeout lets the form store the selected value first.
    const handleSelectChange = () => scheduleSubmit(0, form.getFieldsValue(true), QuoteStore.quote_uid);

    const handleProcessChange = (newManufacturingType: string) => {
        const currentMaterial = getFieldValue(fieldName('material'));
        if (currentMaterial?.key) {
            const material = QuoteStore.materials?.find(m => m.id === Number(currentMaterial.key));
            if (
                material &&
                !material.manufacturing_types.some(type => type.toLowerCase() === newManufacturingType.toLowerCase())
            ) {
                setFieldsValue({
                    [fieldName('material')]: null,
                });
                materialClearedByProcess.current = true;
            }
        }
        handleSelectChange();
    };

    const manufacturing_type = getFieldValue(fieldName('manufacturing_type')) || item?.manufacturing_type || 'CNC';

    const handleOnSelectMaterial = async e => {
        const materialLabel = e?.label || '';
        const materialId = e?.key;

        // Load compatible coatings
        const compatibleCoatings = await QuoteStore.getCoatingsByMaterial(Number(materialId));
        if (compatibleCoatings) setCoatings(compatibleCoatings);

        const currentCoating = getFieldValue(fieldName('coatings'));
        const { clearCoating, modal } = getMaterialSelectionOutcome({
            materialId,
            label: materialLabel,
            currentCoatingKey: currentCoating?.key,
            compatibleCoatings,
            materials: QuoteStore.materials,
        });

        // Coating cleanup (must run before the color question interrupts the flow).
        if (clearCoating) {
            setFieldsValue({
                [fieldName('coatings')]: null,
            });
        }

        if (modal) {
            setShowMaterialColorModal(modal);

            return true;
        }

        // Execute save if no modal interruption occurred
        handleSelectChange();
    };

    // Order the finishes
    // First, spread the 'deburr' finish if it exists.
    const allFinishes: FinishesProps[] = QuoteStore.finishes || [];

    const orderedFinishes: FinishesProps[] = [
        ...(allFinishes.find(finish => finish.name.toLowerCase() === 'deburr')
            ? [allFinishes.find(finish => finish.name.toLowerCase() === 'deburr')!]
            : []),

        ...allFinishes.filter(finish => finish.name.toLowerCase() !== 'deburr'),
    ];

    const quantitySelector = (
        <div>
            <Form.Item name={fieldName('quantity')} initialValue={item.quantity} noStyle>
                <Input type="hidden" />
            </Form.Item>
            <QuantitySelector
                disabled={loading || QuoteStore.isSyncingQuantities}
                quantities={displayedQuantities}
                onChange={quantities => {
                    // Keep THIS card's own quantity — do not overwrite with
                    // quantities[0] (lowest selected), or an unrelated later
                    // save can submit the wrong quantity for this variation.
                    const currentQuantity = Math.max(
                        1,
                        Number(getFieldValue(fieldName('quantity')) ?? item.quantity) || 1
                    );
                    // An empty selection cannot collapse the group (reconcile
                    // exits early on []), so fall back to the current quantity.
                    const normalizedQuantities = quantities.length > 0 ? quantities : [currentQuantity];

                    // Keep the UI responsive while the variation fan-out runs,
                    // then reconcile it with the refreshed quote.
                    setDisplayedQuantities(normalizedQuantities);
                    setFieldsValue({
                        [fieldName('quantity')]: currentQuantity,
                        [fieldName('quantities')]: normalizedQuantities,
                    });
                    // Derive the mode from the selection so collapsing back to a
                    // single quantity clears needs_more_quantities on save instead
                    // of leaving the item stuck in multi-quantity mode.
                    setQuoteMultipleMode(normalizedQuantities.length > 1);
                    QuoteStore.syncQuantities(item, normalizedQuantities);
                }}
            />
        </div>
    );

    return (
        <div
            className="quote-product-variant-container"
            data-line-item-id={item.id}
            data-quantity={item.quantity}
            data-testid="quote-line-item"
            id={`otf-line-item-${item.id}`}
        >
            <Card
                className="quote-product-variant"
                size="small"
                title={
                    <div className="flex items-center">
                        {item.safe_auto_quote ? <Badge color="#52c41a" /> : null}
                        <div className="variant-name">{`Variant #${index + 1}`}</div>
                    </div>
                }
                extra={
                    <Tooltip placement="top" title="Remove Variation">
                        <Button
                            className="remove-variant-btn"
                            title="Remove Variation"
                            icon={<CloseOutlined />}
                            size="small"
                            type="link"
                            disabled={loading || QuoteStore.isSyncingQuantities}
                            onClick={() => {
                                // Multi-quantity groups render one visible card for
                                // several sibling rows — deleting only the base would
                                // leave hidden siblings and inflate totals.
                                if (multiQuantityItems && multiQuantityItems.length > 1) {
                                    QuoteStore.deleteLineItems(multiQuantityItems.map(lineItem => lineItem.uid));
                                    return;
                                }
                                QuoteStore.deleteLineItem(item.uid);
                            }}
                        />
                    </Tooltip>
                }
            >
                <Form form={form} layout="horizontal">
                    {fileType?.toLowerCase() === 'stl' && (
                        <div className="m-3 md:m-5">
                            <Alert
                                // type="info"
                                showIcon={true}
                                message="File Compatibility Message"
                                description="STL files (.stl or .STL) can only be used for quoting 3D-Printed parts. If you part requires a different production method, such as CNC or Sheet Metal, please upload a compatible file type."
                                className="manual-review-required-message"
                            />
                        </div>
                    )}

                    <div className="quote-product-variant-details" ref={variantFormShellRef}>
                        {/*
                        `.quote-product-variant-details` is `display: flex; flex-direction: row`
                        (see styles.css), so any two elements placed directly inside it sit
                        side by side rather than stacked. Wrap the two sections below in a
                        single full-width column container so they stack as: (1) specs
                        row on top, (2) order-frequency + price area underneath.
                    */}
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            {/* Specs row: Process / Material / Surface Finish / Coating laid out
                        side by side across the full width, instead of stacked in a narrow
                        left column squeezed next to the price area. */}
                            <Row gutter={16} style={{ width: '100%' }}>
                                <Col span={24}>
                                    <div
                                        className="quote-product-variant-spec"
                                        style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
                                    >
                                        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                                            <div
                                                className="variant-spec-dropdown-wrapper"
                                                data-testid="quote-variation-process"
                                            >
                                                <Form.Item
                                                    name={fieldName('manufacturing_type')}
                                                    label="Process"
                                                    labelCol={{ span: 24 }}
                                                    rules={[{ required: false, message: 'Required' }]}
                                                    initialValue={item.manufacturing_type}
                                                >
                                                    <Processes
                                                        onSelect={handleProcessChange}
                                                        for_staff={QuoteStore?.for_staff}
                                                        disabled={fileType?.toLowerCase() === 'stl' ? true : false}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>

                                        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                                            <div
                                                className="variant-spec-dropdown-wrapper"
                                                data-testid="quote-variation-material"
                                            >
                                                <Form.Item label="Material" labelCol={{ span: 24 }}>
                                                    <Form.Item
                                                        name={fieldName('material')}
                                                        initialValue={{
                                                            key: product.material_related.id,
                                                            label: product.material_related.name,
                                                        }}
                                                        rules={[{ required: false, message: 'Required' }]}
                                                        hasFeedback={hasFieldError('material')}
                                                        validateStatus={hasFieldError('material') ? 'error' : undefined}
                                                        help={hasFieldError('material') ? 'Material Required' : null}
                                                    >
                                                        <Materials
                                                            onSelect={handleOnSelectMaterial}
                                                            manufacturing_type={manufacturing_type}
                                                            materials={QuoteStore.materials}
                                                            for_staff={QuoteStore?.for_staff}
                                                        />
                                                    </Form.Item>

                                                    {showCustomMaterialInput ? (
                                                        <Form.Item
                                                            name={fieldName('custom_material')}
                                                            rules={[{ required: false, message: 'Required' }]}
                                                            initialValue={product.custom_material}
                                                            hasFeedback={true}
                                                            validateStatus={
                                                                hasFieldError('custom_material') ? 'error' : undefined
                                                            }
                                                            help={
                                                                hasFieldError('custom_material')
                                                                    ? 'Custom Material Name Required'
                                                                    : null
                                                            }
                                                        >
                                                            <Input
                                                                size="small"
                                                                onBlur={handleChange}
                                                                placeholder="Enter Custom Material"
                                                            />
                                                        </Form.Item>
                                                    ) : null}
                                                </Form.Item>
                                            </div>

                                            {suggestionsLoading ? (
                                                <DrawingSuggestionsLoading suggestionType="Material" />
                                            ) : null}

                                            {files_2d && !suggestionsLoading ? (
                                                <SuggestionField
                                                    suggestions={
                                                        product.attachments.files_2d?.suggestions
                                                            ? product.attachments.files_2d.suggestions
                                                                  .filter(s => s.suggested_material?.id)
                                                                  .map(s => {
                                                                      return {
                                                                          id: s.suggested_material?.id ?? 0,
                                                                          name: s.suggested_material?.name ?? '',
                                                                          thumbnail: s.cropped_image?.file ?? '',
                                                                          version: s.version ?? '1.0',
                                                                      };
                                                                  })
                                                            : []
                                                    }
                                                    suggested_text={
                                                        product.attachments.files_2d?.suggested_materials_text
                                                    }
                                                    previewImage={files_2d?.thumbnail}
                                                    suggestionType="Material"
                                                    for_staff={QuoteStore?.for_staff}
                                                    handleClick={async (key: string, label: string) => {
                                                        const getValue = {
                                                            key: key,
                                                            label: label,
                                                        };

                                                        setFieldsValue({
                                                            [fieldName('material')]: getValue,
                                                        });

                                                        // Same path as the dropdown: coatings, color question, then save.
                                                        await handleOnSelectMaterial(getValue);
                                                    }}
                                                />
                                            ) : null}
                                        </div>

                                        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                                            <div
                                                className="variant-spec-dropdown-wrapper"
                                                data-testid="quote-variation-finish"
                                            >
                                                <Form.Item label="Surface Finish" labelCol={{ span: 24 }}>
                                                    <Form.Item
                                                        name={fieldName('finishes')}
                                                        initialValue={{
                                                            key: product.finish_related?.id,
                                                            label: product.finish_related?.name,
                                                        }}
                                                        rules={[{ required: false, message: 'Please select a value' }]}
                                                        hasFeedback={hasFieldError('finishes')}
                                                        validateStatus={hasFieldError('finishes') ? 'error' : undefined}
                                                        help={
                                                            hasFieldError('finishes') ? 'Surface Finish Required' : null
                                                        }

                                                        // disabled={getFieldValue(fieldName('manufacturing_type')) === "3D PRINTING"}
                                                    >
                                                        <Finishes
                                                            finishes={orderedFinishes}
                                                            onSelect={handleSelectChange}
                                                            for_staff={QuoteStore?.for_staff}
                                                        />
                                                    </Form.Item>

                                                    {showCustomFinishInput ? (
                                                        <Form.Item
                                                            name={fieldName('custom_finish')}
                                                            rules={[
                                                                { required: false, message: 'Please insert a value' },
                                                            ]}
                                                            initialValue={product?.custom_finish}
                                                            hasFeedback={true}
                                                            validateStatus={
                                                                hasFieldError('custom_finish') ? 'error' : undefined
                                                            }
                                                            help={
                                                                hasFieldError('custom_finish')
                                                                    ? 'Custom Surface Finish Name Required'
                                                                    : null
                                                            }
                                                        >
                                                            <Input
                                                                size="small"
                                                                onBlur={handleChange}
                                                                placeholder="Enter Custom Surface Finish"
                                                            />
                                                        </Form.Item>
                                                    ) : null}
                                                </Form.Item>
                                            </div>

                                            {suggestionsLoading ? (
                                                <DrawingSuggestionsLoading suggestionType="Finish" />
                                            ) : null}

                                            {files_2d && !suggestionsLoading ? (
                                                <SuggestionField
                                                    suggestions={
                                                        product.attachments.files_2d?.suggestions
                                                            ? product.attachments.files_2d.suggestions
                                                                  .slice()
                                                                  .sort(s =>
                                                                      s.suggested_finish?.name === 'Deburr' ? -1 : 1
                                                                  )
                                                                  .sort(s =>
                                                                      s.suggested_finish?.name === 'None' ? -1 : 1
                                                                  )
                                                                  .filter(s => s.suggested_finish?.id)
                                                                  .map(s => {
                                                                      return {
                                                                          id: s.suggested_finish?.id ?? 0,
                                                                          name: s.suggested_finish?.name ?? '',
                                                                          thumbnail: s.cropped_image?.file ?? '',
                                                                          version: s.version ?? '1',
                                                                      };
                                                                  })
                                                            : []
                                                    }
                                                    suggested_text={
                                                        product.attachments.files_2d?.suggested_finishes_text
                                                    }
                                                    previewImage={files_2d?.thumbnail}
                                                    suggestionType="Finish"
                                                    for_staff={QuoteStore?.for_staff}
                                                    handleClick={async (key: string, label: string) => {
                                                        const getValue = {
                                                            key: key,
                                                            label: label,
                                                        };

                                                        setFieldsValue({
                                                            [fieldName('finishes')]: getValue,
                                                        });

                                                        handleSelectChange();
                                                    }}
                                                />
                                            ) : null}
                                        </div>

                                        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                                            <div
                                                className="variant-spec-dropdown-wrapper"
                                                data-testid="quote-variation-coating"
                                            >
                                                <Form.Item label="Coating" labelCol={{ span: 24 }}>
                                                    <Form.Item
                                                        name={fieldName('coatings')}
                                                        initialValue={{
                                                            key: product?.coating_related?.id,
                                                            label: product?.coating_related?.name,
                                                        }}
                                                        rules={[{ required: false, message: 'Please select a value' }]}
                                                        hasFeedback={hasFieldError('coatings')}
                                                        validateStatus={hasFieldError('coatings') ? 'error' : undefined}
                                                        help={hasFieldError('coatings') ? 'Coating Required' : null}
                                                    >
                                                        <Coatings
                                                            coatings={coatings}
                                                            onSelect={handleSelectChange}
                                                            for_staff={QuoteStore?.for_staff}
                                                        />
                                                    </Form.Item>

                                                    {showCustomCoatingInput ? (
                                                        <Form.Item
                                                            name={fieldName('custom_coating')}
                                                            initialValue={product?.custom_coating}
                                                            rules={[
                                                                { required: false, message: 'Please insert a value' },
                                                            ]}
                                                            hasFeedback={true}
                                                            validateStatus={
                                                                hasFieldError('custom_coating') ? 'error' : undefined
                                                            }
                                                            help={
                                                                hasFieldError('custom_coating')
                                                                    ? 'Custom Coating Name Required'
                                                                    : null
                                                            }
                                                        >
                                                            <Input
                                                                size="small"
                                                                onBlur={handleChange}
                                                                placeholder="Enter Custom Coating"
                                                            />
                                                        </Form.Item>
                                                    ) : null}
                                                </Form.Item>
                                            </div>

                                            <div className="d-block">
                                                {suggestionsLoading ? (
                                                    <DrawingSuggestionsLoading suggestionType="Coating" />
                                                ) : null}

                                                {files_2d && !suggestionsLoading ? (
                                                    <SuggestionField
                                                        suggestions={
                                                            product.attachments.files_2d?.suggestions
                                                                ? product.attachments.files_2d.suggestions
                                                                      .slice()
                                                                      .sort(s =>
                                                                          s.suggested_coating?.name === 'Deburr'
                                                                              ? -1
                                                                              : 1
                                                                      )
                                                                      .sort(s =>
                                                                          s.suggested_coating?.name === 'None' ? -1 : 1
                                                                      )
                                                                      .filter(s => s.suggested_coating?.id)
                                                                      .map(s => {
                                                                          return {
                                                                              id: s.suggested_coating?.id ?? 0,
                                                                              name: s.suggested_coating?.name ?? '',
                                                                              thumbnail: s.cropped_image?.file ?? '',
                                                                              version: s.version ?? '1.0',
                                                                          };
                                                                      })
                                                                : []
                                                        }
                                                        suggested_text={
                                                            product.attachments.files_2d?.suggested_coatings_text
                                                        }
                                                        previewImage={files_2d?.thumbnail}
                                                        suggestionType="Coating"
                                                        for_staff={QuoteStore?.for_staff}
                                                        handleClick={async (key: string, label: string) => {
                                                            const getValue = {
                                                                key: key,
                                                                label: label,
                                                            };

                                                            setFieldsValue({
                                                                [fieldName('coatings')]: getValue,
                                                            });

                                                            handleSelectChange();
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Order frequency + quantity mode sit as extra items in the same
                                    wrapping spec row now that removing the left preview sidebar freed up
                                    enough width — instead of a separate row squeezed below. */}
                                        {QuoteStore.for_staff ? (
                                            <>
                                                <div
                                                    data-testid="quote-variation-order-frequency"
                                                    style={{ flex: '1 1 160px', minWidth: 160 }}
                                                >
                                                    <Form.Item
                                                        name={fieldName('quantity_frequency')}
                                                        rules={[
                                                            {
                                                                required: false,
                                                                message: 'Please select a frequency',
                                                            },
                                                        ]}
                                                        labelCol={{ span: 24 }}
                                                        style={{ marginBottom: 0 }}
                                                        initialValue={initialOrderFrequencyFromLineItem(item)}
                                                        label={
                                                            <>
                                                                Order frequency
                                                                <QuestionMark>
                                                                    How often you need this quantity. Recurring options
                                                                    (monthly, quarterly, yearly) indicate batch or
                                                                    blanket releases.
                                                                </QuestionMark>
                                                            </>
                                                        }
                                                    >
                                                        <FrequencySelector
                                                            hideLabel
                                                            onChange={() => handleSelectChange()}
                                                        />
                                                    </Form.Item>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </Col>
                            </Row>

                            {/* Price area, full width below the specs + order-frequency/quantity row. */}
                            <Row gutter={16} style={{ width: '100%', marginTop: 8 }}>
                                <Col span={24}>
                                    <div className="quote-product-variant-qty-price">
                                        {QuoteStore.for_staff ? (
                                            <div className="w-full">
                                                {quoteMultipleMode && multiQuantityItems?.length ? (
                                                    <Spin spinning={isSaving || QuoteStore.isSyncingQuantities}>
                                                        <MultipleQuantitiesPrices
                                                            items={multiQuantityItems}
                                                            quantitySelector={quantitySelector}
                                                        />
                                                    </Spin>
                                                ) : isSafeAutoQuote ? (
                                                    <Spin spinning={isSaving || QuoteStore.isSyncingQuantities}>
                                                        <LeadTimeOptions
                                                            lineItem={item}
                                                            quantitySelector={quantitySelector}
                                                        />
                                                    </Spin>
                                                ) : (
                                                    <Spin
                                                        spinning={isSaving || QuoteStore.isSyncingQuantities}
                                                        indicator={<LoadingOutlined />}
                                                    >
                                                        <div className="mb-2">
                                                            <div className="font-semibold text-gray-700 text-base">
                                                                Quantities to quote
                                                            </div>
                                                            <div className="text-gray-600 font-size-sm">
                                                                Choose one price and lead time for each quantity you may
                                                                want to order.
                                                            </div>
                                                            <div className="mt-2">{quantitySelector}</div>
                                                        </div>
                                                        {isPricingInProgress ? (
                                                            <CalculatingPriceState />
                                                        ) : (
                                                            <ManualQuoteRequiredAlert
                                                                quantity={item.quantity}
                                                                lineItemId={item.id}
                                                            />
                                                        )}
                                                    </Spin>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col-reverse xl:flex-row justify-content-between xl:ml-5">
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        width: '100%',
                                                        padding: '0 10px',
                                                    }}
                                                >
                                                    <div className="font-semibold text-gray-700 text-base">
                                                        <label htmlFor="quantity" className="mr-2">
                                                            Lead Time Options:
                                                        </label>
                                                    </div>

                                                    {isSafeAutoQuote ? (
                                                        <Spin spinning={isSaving}>
                                                            <LeadTimeOptions lineItem={item} />
                                                        </Spin>
                                                    ) : isPricingInProgress ? (
                                                        <Spin spinning={isSaving} indicator={<LoadingOutlined />}>
                                                            <CalculatingPriceState />
                                                        </Spin>
                                                    ) : (
                                                        <Spin spinning={isSaving} indicator={<LoadingOutlined />}>
                                                            <ManualQuoteRequiredAlert
                                                                quantity={item.quantity}
                                                                lineItemId={item.id}
                                                            />
                                                        </Spin>
                                                    )}
                                                </div>

                                                <div className="quantity-selector d-flex flex-col">
                                                    <div className="mb-2 font-semibold text-gray-700 text-base">
                                                        <label htmlFor="quantity" className="mr-2">
                                                            Quantity
                                                        </label>
                                                    </div>
                                                    <div className="button-group" style={{ height: '36px' }}>
                                                        <Button
                                                            size="small"
                                                            className="minus-btn"
                                                            icon={<MinusOutlined />}
                                                            onClick={() => {
                                                                const currentQuantity =
                                                                    Number(
                                                                        getFieldValue(fieldName('quantity')) ??
                                                                            item.quantity
                                                                    ) || min;
                                                                setFieldsValue({
                                                                    [fieldName('quantity')]: Math.max(
                                                                        min,
                                                                        currentQuantity - 1
                                                                    ),
                                                                });

                                                                handleSubmit();
                                                            }}
                                                            disabled={
                                                                loading ||
                                                                Number(getFieldValue(fieldName('quantity'))) === min
                                                            }
                                                        />

                                                        <Form.Item
                                                            name={fieldName('quantity')}
                                                            initialValue={item.quantity}
                                                            noStyle
                                                        >
                                                            <InputNumber
                                                                min={min}
                                                                step={1}
                                                                controls={false}
                                                                size="small"
                                                                onChange={value => {
                                                                    setFieldsValue({
                                                                        [fieldName('quantity')]: value,
                                                                    });
                                                                }}
                                                                onBlur={handleChange}
                                                                disabled={loading}
                                                            />
                                                        </Form.Item>

                                                        <Button
                                                            size="small"
                                                            className="plus-btn"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => {
                                                                const currentQuantity =
                                                                    Number(
                                                                        getFieldValue(fieldName('quantity')) ??
                                                                            item.quantity
                                                                    ) || min;
                                                                setFieldsValue({
                                                                    [fieldName('quantity')]: currentQuantity + 1,
                                                                });

                                                                handleSubmit();
                                                            }}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* <PriceRender price={item.unit_price} /> */}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                    {showMaterialColorModal && (
                        <PlasticMaterialPreferencesModal
                            showMaterialColorModal={showMaterialColorModal}
                            handleSubmit={() => handleSubmit()}
                            handleModalCancel={handleModalCancel}
                            fieldName={fieldName}
                        />
                    )}
                </Form>
            </Card>

            {/*{QuoteStore.for_staff && item.safe_auto_quote && item.prices_by_quantity !== null && !QuoteStore.isRequote && (*/}
            {/*    <>*/}
            {/*        {item.prices_by_quantity.length > 1 && (*/}
            {/*            <div className={'flex flex-row'}>*/}
            {/*                <div className={'w-2/3'} />*/}
            {/*                <div className={'w-1/3'}>*/}
            {/*                    <div className={'flex flex-row'}>*/}
            {/*                        <div className={'flex flex-row w-full'}>*/}
            {/*                            <div className={'w-1/4 mr-4'}>*/}
            {/*                                <div className={'flex justify-center font-semibold'}>Quantity</div>*/}
            {/*                            </div>*/}

            {/*                            <div className={'w-1/4 mr-4'}>*/}
            {/*                                <div className={'flex justify-center font-semibold'}>Lead Time</div>*/}
            {/*                            </div>*/}

            {/*                            <div className={'w-1/4 mr-4'}>*/}
            {/*                                <div className={'flex justify-center font-semibold'}>Unit Price</div>*/}
            {/*                            </div>*/}

            {/*                            <div className={'w-1/4 mr-4'}>*/}
            {/*                                <div className={'flex justify-center font-semibold'}>Total Price</div>*/}
            {/*                            </div>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                    {item.prices_by_quantity.map((qt: PricesByQuantity, index) => {*/}
            {/*                        return (*/}
            {/*                            <>*/}
            {/*                                {qt.quantity !== item.quantity && (*/}
            {/*                                    <QuoteLineQuantityItem*/}
            {/*                                        key={index}*/}
            {/*                                        item={{*/}
            {/*                                            quantity: qt.quantity,*/}
            {/*                                            lead_time: qt.lead_time,*/}
            {/*                                            unit_price: qt.unit_price,*/}
            {/*                                            total_price: qt.total_price,*/}
            {/*                                            safe_auto_quote: qt.safe_auto_quote,*/}
            {/*                                        }}*/}
            {/*                                    />*/}
            {/*                                )}*/}
            {/*                            </>*/}
            {/*                        );*/}
            {/*                    })}*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        )}*/}
            {/*    </>*/}
            {/*)}*/}
        </div>
    );
});

export default QuoteLineItem;
